/*======================================================================================================================
  * Bluebell: src/app/state/kinematic-models/kinematic-model.reducer.ts
  * Last Edited: 15/01/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { programmeProjectSelectionUnapplied } from '@state/account/account.actions';
import {
  clearSelectedKinematicModel,
  createKinematicModel,
  createKinematicModelFailure,
  createKinematicModelSuccess,
  deleteKinematicModelSuccess,
  listKinematicModels,
  listKinematicModelsFailure,
  listKinematicModelsSuccess,
  retrieveKinematicModel,
  retrieveKinematicModelFailure,
  retrieveKinematicModelSuccess,
  updateKinematicModel,
  updateKinematicModelFailure,
  updateKinematicModelSuccess,
} from '@state/kinematic-models/kinematic-model.actions';
import { KinematicModel, KinematicModelBase } from '@state/kinematic-models/kinematic-model.interfaces';


export interface State extends EntityState<KinematicModelBase> {
  loading: boolean;
  loaded: boolean;
  selected: KinematicModel|null;
  selectedLoading: boolean;
}

export const adapter: EntityAdapter<KinematicModelBase> = createEntityAdapter<KinematicModelBase>({
  selectId: (model) => model.uuid,
});

export const initialState: State = adapter.getInitialState<State>({
  loading: false,
  loaded: false,
  selected: null,
  selectedLoading: false,
});

export const reducer = createReducer(
  initialState,

  /* Create */

  on(createKinematicModel, (state) => {
    return {
      ...state,
      selected: null,
      selectedLoading: true,
    };
  }),

  on(createKinematicModelSuccess, (state, { selected }) => {
    return adapter.addOne(
      { name: selected.name, uuid: selected.uuid },
      {
        ...state,
        selected,
        selectedLoading: false,
      }
    );
  }),

  on(createKinematicModelFailure, (state) => {
    return {
      ...state,
      selected: null,
      selectedLoading: false,
    };
  }),

  /* List */

  on(listKinematicModels, (state) => {
    return adapter.removeAll({
      ...state,
      loading: true,
    });
  }),

  on(listKinematicModelsSuccess, (state, { models }) => {
    return adapter.setAll(
      models,
      {
        ...state,
        loaded: true,
        loading: false,
      }
    );
  }),

  on(listKinematicModelsFailure, (state) => {
    return {
      ...state,
      loading: false,
    };
  }),

  /* Retrieve */

  on(retrieveKinematicModel, (state) => {
    return {
      ...state,
      selectedLoading: true,
    };
  }),

  on(retrieveKinematicModelSuccess, (state, { selected }) => {
    return {
      ...state,
      selected,
      selectedLoading: false,
    };
  }),

  on(retrieveKinematicModelFailure, (state) => {
    return {
      ...state,
      selectedLoading: false,
    };
  }),

  /* Update */

  on(updateKinematicModel, (state) => {
    return {
      ...state,
      selectedLoading: true,
    };
  }),

  on(updateKinematicModelSuccess, (state, { updatedModel }) => {
    return adapter.updateOne(
      {
        id: updatedModel.uuid,
        changes: {
          name: updatedModel.name,
        }
      },
      {
        ...state,
        selected: updatedModel,
        selectedLoading: false,
      }
    );
  }),

  on(updateKinematicModelFailure, (state) => {
    return {
      ...state,
      selected: null,
      selectedLoading: true,
    };
  }),

  /* Delete */

  on(deleteKinematicModelSuccess, (state, { uuid }) => {
    return adapter.removeOne(
      uuid,
      {
        ...state,
        selected: state.selected && state.selected.uuid === uuid ? null : state.selected,
      },
    );
  }),

  /* Misc. */

  on(clearSelectedKinematicModel, (state) => {
    return {
      ...state,
      selected: null,
      selectedLoading: false,
    };
  }),

  on(programmeProjectSelectionUnapplied, () => {
    return adapter.removeAll({ ...initialState });
  }),
);

export const {
  selectIds: getIds,
  selectEntities: getEntities,
  selectTotal: getTotal,
  selectAll: getAll,
} = adapter.getSelectors();

export const featureKey = 'kinematicModels';
export const getLoading = (state: State) => state.loading;
export const getLoaded = (state: State) => state.loaded;
export const getSelected = (state: State) => state.selected;
export const getSelectedLoading = (state: State) => state.selectedLoading;
